import { useCallback, useId, useState } from 'react'
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react'
import { Icon } from './Icon'

interface CollapsibleProps {
  trigger: ReactNode
  children: ReactNode
  defaultOpen?: boolean
  /** Controlled open state. When provided, must pair with `onOpenChange`. */
  open?: boolean
  onOpenChange?: (next: boolean) => void
}

/**
 * Lightweight expand/collapse primitive. Operates uncontrolled by default
 * (`defaultOpen`) or controlled via `open` + `onOpenChange`. Trigger is a
 * full-width button row with a rotating chevron; body fades + reveals in
 * ≤150 ms (per UI/UX standard).
 */
export function Collapsible({
  trigger,
  children,
  defaultOpen = false,
  open,
  onOpenChange,
}: CollapsibleProps): JSX.Element {
  const [internalOpen, setInternalOpen] = useState<boolean>(defaultOpen)
  const [hover, setHover] = useState<boolean>(false)
  const isControlled = typeof open === 'boolean'
  const isOpen = isControlled ? open : internalOpen
  const bodyId = useId()

  const setOpen = useCallback(
    (next: boolean): void => {
      if (!isControlled) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange],
  )

  const toggle = useCallback((): void => {
    setOpen(!isOpen)
  }, [isOpen, setOpen])

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault()
        toggle()
      } else if (event.key === 'Escape' && isOpen) {
        event.preventDefault()
        setOpen(false)
      }
    },
    [isOpen, setOpen, toggle],
  )

  const triggerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    textAlign: 'left',
    padding: '8px 12px',
    minHeight: 44,
    background: hover ? 'var(--surface-2)' : 'transparent',
    border: '1px solid var(--border)',
    borderRadius: 10,
    color: 'var(--text)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition:
      'background var(--motion-duration-base) var(--motion-ease-out),' +
      ' border-color var(--motion-duration-base) var(--motion-ease-out),' +
      ' color var(--motion-duration-base) var(--motion-ease-out)',
  }

  return (
    <div data-testid="collapsible" data-open={isOpen ? 'true' : 'false'}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={toggle}
        onKeyDown={onKeyDown}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={triggerStyle}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            transition:
              'transform var(--motion-duration-base) var(--motion-ease-out)',
            transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            color: 'var(--text-3)',
          }}
        >
          <Icon name="chevron_right" size={18} />
        </span>
        <span style={{ flex: 1 }}>{trigger}</span>
      </button>
      <div
        id={bodyId}
        role="region"
        hidden={!isOpen}
        style={{
          overflow: 'hidden',
          maxHeight: isOpen ? 'none' : 0,
          opacity: isOpen ? 1 : 0,
          transition:
            'opacity var(--motion-duration-base) var(--motion-ease-out)',
          paddingTop: isOpen ? 8 : 0,
        }}
      >
        {isOpen ? children : null}
      </div>
    </div>
  )
}
